// "Your phone is not going to buzz" — said before a line stops rather than
// after.
//
// Ported from the Andon app. The reported symptom it exists for is people not
// getting notifications and nobody, including them, being able to tell. Every
// way push can fail used to look identical to push working: same floor
// screen, same signed-in name, no bell struck through, nothing. `lib/pushHealth`
// gives each failure a name and this is where three of them get said out loud.
//
//   ask           never been asked. One tap, and the tap matters — see below.
//   unregistered  allowed, but there is no live subscription the server has
//                 acknowledged. Retryable, same button.
//   blocked       denied. There is NO button that can fix this, and pretending
//                 otherwise is worse than saying where the switch is.
//
// WHY THE BUTTON EXISTS AT ALL, WHEN THE APP COULD JUST ASK
// --------------------------------------------------------
// Because on iOS it cannot. `Notification.requestPermission()` without a user
// gesture is refused outright by WebKit, and that is precisely what a
// boot-time call would be — so on the platform where push is most fragile,
// the dialog would never appear. Chromium does allow it, and answers an
// unprompted request with its quiet permission UI and a good chance of a
// permanent block. A tap on a button that has just explained itself is a
// better request on every platform, and the only workable one on one of them.
//
// WHY `needs-install` IS NOT HANDLED HERE
// ---------------------------------------
// On iOS in a tab there is no Web Push at all, and the fix is not a permission
// — it is putting the app on the home screen, which is what InstallBanner
// already says, in the platform's own words. Two banners saying overlapping
// things is how people learn to dismiss both, so this one stays quiet whenever
// there is still an install to offer.
//
// Only ever mounted with the andon preview on: without it this app offers no
// push, and a banner about notifications it cannot send would be a lie.

import { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { useT } from '@/i18n/useT';
import { isStandalone } from '@/lib/installPrompt';
import { pushCopy, pushHealth, pushNeedsAttention, pushSnoozed, snoozePush, subscribePushHealth, type PushHealth } from '@/lib/pushHealth';
import { enablePush } from '@/pwa';

export function PushBanner() {
  const t = useT();
  // Not useSyncExternalStore, matching InstallBanner: the module is readable
  // before React is involved, because the answer comes from the browser and the
  // service worker rather than from us.
  const [health, setHealth] = useState<PushHealth>(() => pushHealth());
  const [hidden, setHidden] = useState(() => pushSnoozed());
  const [busy, setBusy] = useState(false);
  const install = useInstallPrompt();

  useEffect(() => subscribePushHealth(() => setHealth(pushHealth())), []);

  // An install still on offer means the install banner is the right message,
  // and on iOS it is the ONLY one that would help.
  if (install.method !== 'none') return null;
  if (hidden || !pushNeedsAttention(health)) return null;

  const copy = pushCopy(health, isStandalone());

  const dismiss = () => {
    snoozePush();
    setHidden(true);
  };

  const turnOn = () => {
    setBusy(true);
    // The health module is what re-renders this — including into nothing, when
    // the answer is `ok`. A rejected dialog leaves `blocked` and the banner
    // rewrites itself into the instructions, which is the honest outcome and
    // the one somebody needs immediately after tapping Block by accident.
    void enablePush().finally(() => setBusy(false));
  };

  const blocked = health === 'blocked';

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top) + 8px + var(--sf-banner, 0px))',
        left: 10,
        right: 10,
        // Under the sheets, and under the update banner: a screen with two
        // banners on it has neither.
        zIndex: 24,
        padding: '11px 12px 11px 14px',
        borderRadius: 15,
        background: 'var(--surface)',
        // Blocked is the state nobody can fix by tapping this, so it is the
        // only one that borrows the danger colour. The other two are one tap
        // from fine and must not read as an emergency on a factory floor.
        border: `1px solid ${blocked ? 'var(--danger)' : 'var(--line)'}`,
        boxShadow: '0 8px 24px rgba(22,24,28,.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        animation: 'ipRise .3s ease',
      }}
    >
      <Icon
        name={blocked ? 'warn' : 'bell'}
        size={17}
        strokeWidth={2.2}
        style={{ color: blocked ? 'var(--danger)' : 'var(--accent)', flex: 'none' }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: "700 13px 'Space Grotesk',sans-serif", color: 'var(--ink)' }}>{t[copy.title]}</span>
        <span style={{ display: 'block', font: '500 11px/1.45 Manrope,sans-serif', color: 'var(--sub)', marginTop: 1 }}>{t[copy.body]}</span>
      </span>
      {copy.action && (
        <button
          type="button"
          className="press"
          onClick={turnOn}
          disabled={busy}
          style={{
            height: 36,
            padding: '0 14px',
            borderRadius: 11,
            background: 'var(--accent)',
            color: 'var(--accentInk)',
            border: 0,
            font: "600 12px 'Space Grotesk',sans-serif",
            flex: 'none',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? t.askingT : t[copy.action]}
        </button>
      )}
      {/* A 36px target, not a 16px glyph. This is tapped with a glove on. */}
      <button
        type="button"
        className="press"
        aria-label={t.notNowT}
        onClick={dismiss}
        style={{
          width: 36,
          height: 36,
          borderRadius: 11,
          background: 'var(--surface2)',
          border: '1px solid var(--line)',
          color: 'var(--sub)',
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
        }}
      >
        <Icon name="cross" size={13} strokeWidth={2.6} />
      </button>
    </div>
  );
}
